import { memo, useMemo, useRef } from 'react';
import type { FC, MouseEventHandler } from 'react';

import type { AssetSplashComponent } from '@vgtv/api-client/lib/component/splash';
import { useTranslation } from 'react-i18next';

import { Splash } from '../Splash/Splash';
import { useComponentAssets } from '../../utils/useComponentAssets';
import {
  getAssetDisplayTitle,
  getAssetMainImage,
  getAssetMetadata,
  getAssetPromoUrl,
  isCurrentlyLive,
} from '../../../shared/helpers/assetHelpers';
import { useServerTime } from '../../utils/useServerTime';
import { Badge } from '../../Asset/shared/Badge/Badge';
import { UpcomingLiveBadge } from '../../Asset/shared/UpcomingLiveBadge/UpcomingLiveBadge';
import { getImageSources } from '../../../helpers/images';
import { isMobile } from '../../../utils/device';
import { AssetButtons } from '../SplashButtons/AssetButtons';
import { useSplashTracking } from '../../PageComponents/common/useSplashTracking';
import { useViewObserver } from '../../utils/useViewObserver';

import { getAssetLabels } from './getAssetLabels';

interface AssetSplashProps {
  componentConfig: AssetSplashComponent;
  isFirstComponent: boolean;
  pageComponentIndex: number;
  overridePlayButtonClick: MouseEventHandler;
}

const AssetSplashBase: FC<AssetSplashProps> = (props: AssetSplashProps) => {
  const {
    componentConfig,
    isFirstComponent,
    pageComponentIndex,
    overridePlayButtonClick,
  } = props;
  const { id, overrides = {} } = componentConfig;
  const { serverTime } = useServerTime();
  const { t } = useTranslation();

  const ref = useRef<HTMLDivElement>(null!);

  const { trackSplashView, trackButtonSplashClick } = useSplashTracking({
    elementType: 'AssetSplash',
    pageComponentIndex,
  });

  useViewObserver({
    onView: trackSplashView,
    elementRef: ref,
  });

  // no SVP fetch since the asset is preset
  // in case of synthetic component, it's preset does not exist in redux store
  const asset = useComponentAssets(id)[0] || componentConfig.data.preset[0];

  const image = overrides.image || getAssetMainImage(asset);
  const title = overrides.title || getAssetDisplayTitle(asset);
  const description = overrides.description || asset.description;
  const previewUrl = getAssetPromoUrl(asset);
  const isLive = isCurrentlyLive(asset, serverTime);
  const { category } = asset;
  const verticalImage = getAssetMetadata(asset).verticalPosterImage;

  const isPulledUp = isFirstComponent && image !== undefined && !isMobile;

  const labels = useMemo(() => getAssetLabels(asset), [asset]);

  const verticalImageSources = useMemo(
    () =>
      getImageSources({
        imageUrl: verticalImage,
        aspectRatio: 'vertical',
        imagePreset: 'assetsSliderVertical',
      }),
    [verticalImage]
  );

  const shouldDisplayLabels = !isLive;

  return (
    <Splash
      isPulledUp={isPulledUp}
      ref={ref}
      imageUrl={image}
      title={title}
      description={description}
      lazy={!isFirstComponent}
      buttons={
        <AssetButtons
          asset={asset}
          trackSplashClick={trackButtonSplashClick}
          overridePlayButtonClick={overridePlayButtonClick}
        />
      }
      subtitle={!isLive ? category.title : undefined}
      previewUrl={previewUrl}
      labels={shouldDisplayLabels ? labels : []}
      badge={
        <>
          {isLive && <Badge kind="live" label={t('Live')} />}
          <UpcomingLiveBadge asset={asset} />
        </>
      }
      verticalImageSources={
        !isLive && verticalImage ? verticalImageSources : undefined
      }
    />
  );
};

export const AssetSplash = memo(AssetSplashBase);
